import { combineEpics, ofType } from "redux-observable"
import { map, flatMap, filter, catchError, concatMap } from "rxjs/operators"
import { of, from, concat, EMPTY } from "rxjs"
import { Epic } from "../../epics"

import * as actions from "./actions"
import { addErrorNotification } from "../notifications/actions"
import { RequestCancelledError } from "../../errors"
import { IContract, IGlobalSettings, IReportData } from "./index"

export const defaultErrorMessage =
    "Something went wrong, could not enable or disable the contract."
export const repairErrorMessage =
    "Something went wrong, could not repair the contract."

export const initializeContractsEpic: Epic = (
    action$,
    state$,
    _dependencies$,
) =>
    action$.pipe(
        ofType(actions.INITIALIZE),
        filter(() => {
            const {
                contracts: { initialized },
            } = state$.value
            return !initialized
        }),
        flatMap(() =>
            concat(
                of(actions.requestGlobalSettings()),
                of(actions.requestContracts(false)),
                of(actions.requestContracts(true)),
                of(actions.setInitialized()),
            ),
        ),
    )

export const requestContractsEpic: Epic = (
    action$,
    _state$,
    { contracts: { contractsAPI } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_CONTRACTS),
        concatMap((action: actions.RequestContracts) =>
            from(contractsAPI.collectContracts(action.validate)),
        ),
        flatMap((configurations: IContract[]) =>
            of(actions.receiveContracts(configurations)),
        ),
    )

export const enableContractEpic: Epic = (
    action$,
    _state$,
    { contracts: { contractsAPI } },
) =>
    action$.pipe(
        ofType(actions.ENABLE_CONTRACT),
        flatMap((action: actions.EnableContract) =>
            from(contractsAPI.enableContract(action.id, action.enabled)).pipe(
                flatMap(() =>
                    concat(
                        of(actions.requestContracts(false)),
                        of(actions.requestContracts(true)),
                    ),
                ),
                catchError((error: Response | Error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    if ("json" in error && error.json) {
                        return from(error.json()).pipe(
                            flatMap(({ Message }) =>
                                of(
                                    addErrorNotification(
                                        Message || defaultErrorMessage,
                                    ),
                                ),
                            ),
                        )
                    }
                    return of(addErrorNotification(defaultErrorMessage))
                }),
            ),
        ),
    )

export const getContractEpic: Epic = (
    action$,
    _state$,
    { contracts: { contractsAPI } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_CONTRACT),
        flatMap((action: actions.ContractIdentifier) =>
            from(contractsAPI.getContractDetails(action.id)),
        ),
        map((contract: IContract) => actions.receiveContract(contract)),
    )

export const validateContractEpic: Epic = (
    action$,
    _state$,
    { contracts: { contractsAPI } },
) =>
    action$.pipe(
        ofType(actions.VALIDATE_CONTRACT),
        flatMap((action: actions.ContractIdentifier) =>
            from(contractsAPI.validateContract(action.id)),
        ),
        map((report: IReportData) => actions.receiveReport(report)),
    )

export const repairContractEpic: Epic = (
    action$,
    _state$,
    { contracts: { contractsAPI } },
) =>
    action$.pipe(
        ofType(actions.REPAIR_CONTRACT),
        flatMap((action: actions.ContractIdentifier) =>
            from(contractsAPI.repairContract(action.id)),
        ),
        map((report: IReportData) => actions.receiveReport(report)),
        catchError((error: Response | Error) => {
            if (error instanceof RequestCancelledError) {
                return EMPTY
            }
            if ("json" in error && error.json) {
                return from(error.json()).pipe(
                    flatMap(({ errorMessage }) =>
                        of(
                            addErrorNotification(
                                repairErrorMessage + "\n" + errorMessage,
                            ),
                        ),
                    ),
                )
            }
            return of(addErrorNotification(repairErrorMessage))
        }),
    )

export const receiveReportEpic: Epic = (action$, _state$, _dependencies$) =>
    action$.pipe(
        ofType(actions.RECEIVE_REPORT),
        filter((action: actions.ReceiveReport) => action.data?.repaired),
        flatMap(() => of(actions.requestContracts(true))),
    )

export const getGlobalSettingsEpic: Epic = (
    action$,
    _state$,
    { contracts: { contractsAPI } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_GLOBAL_SETTINGS),
        flatMap(() => from(contractsAPI.getContractsConfiguration())),
        map((settings: IGlobalSettings) =>
            actions.receiveGlobalSettings(settings),
        ),
    )

export const setGlobalSettingsEpic: Epic = (
    action$,
    _state$,
    { contracts: { contractsAPI } },
) =>
    action$.pipe(
        ofType(actions.UPDATE_GLOBAL_SETTINGS),
        flatMap((action: actions.GlobalSettings) =>
            from(contractsAPI.setContractsConfiguration(action.settings)),
        ),
        flatMap(() => of(actions.requestGlobalSettings())),
    )

export default combineEpics(
    enableContractEpic,
    getContractEpic,
    getGlobalSettingsEpic,
    initializeContractsEpic,
    receiveReportEpic,
    repairContractEpic,
    requestContractsEpic,
    setGlobalSettingsEpic,
    validateContractEpic,
)
